import { useState } from 'react';
import '../../assets/css/custom-select.css';
import billapp from '../../assets/img/billapp.png';
import homeapp from '../../assets/img/homeapp.png';
import orderapp from '../../assets/img/orderapp.png';
import styles from '../../assets/scss/pages/HomePage.module.scss';
import FloatingContact from '../../components/public/FloatingContact';
import useScrollReveal from '../../hooks/useScrollReveal';
import useIsMobile from '../../hooks/useIsMobile';
import EmailService from '../../services/emailService';

const cx = (...keys) =>
    keys
        .filter(Boolean)
        .map((k) => styles[k] || k)
        .join(' ');

// Số Zalo dùng cho các nút tư vấn / chọn gói (chỉnh tại đây khi cần đổi số)
const ZALO_PHONE = '0387556219';
const ZALO_LINK = `https://zalo.me/${ZALO_PHONE}`;

const INDUSTRIES = [
    {
        id: 'salon',
        label: 'Salon & Tóc',
        icon: 'fa-scissors',
        title: 'Salon tóc, makeup',
        desc: 'Quản lý lịch hẹn, thợ phụ trách, hoa hồng theo từng dịch vụ và chăm sóc khách quay lại.',
        points: [
            'Đặt & nhắc lịch hẹn tự động',
            'Hoa hồng theo thợ và dịch vụ',
            'Thẻ tích điểm, gói dịch vụ',
            'Lịch sử dịch vụ từng khách',
        ],
        img: `${process.env.PUBLIC_URL}/salon.png`,
    },
    {
        id: 'spa',
        label: 'Spa & Thẩm mỹ',
        icon: 'fa-spa',
        title: 'Spa, thẩm mỹ viện',
        desc: 'Theo dõi gói dịch vụ nhiều buổi, trả trước và công nợ khách hàng rõ ràng.',
        points: [
            'Gói dịch vụ trừ buổi tự động',
            'Quản lý công nợ, trả trước',
            'Phân ca & lương kỹ thuật viên',
            'Đánh giá, phản hồi từ khách',
        ],
        img: `${process.env.PUBLIC_URL}/spa.png`,
    },
    {
        id: 'nails',
        label: 'Nails',
        icon: 'fa-hand-sparkles',
        title: 'Tiệm nail',
        desc: 'Quản lý mẫu nail, đặt lịch theo thợ và tính hoa hồng cho từng kỹ thuật viên rõ ràng.',
        points: [
            'Đặt & nhắc lịch hẹn theo thợ',
            'Hoa hồng theo mẫu, dịch vụ',
            'Thư viện mẫu nail, bảng giá',
            'Thẻ tích điểm, khách quay lại',
        ],
        img: `${process.env.PUBLIC_URL}/nails.png`,
    },
    {
        id: 'kinhmat',
        label: 'Mắt kính',
        icon: 'fa-glasses',
        title: 'Cửa hàng mắt kính',
        desc: 'Quản lý gọng, tròng kính theo thuộc tính, độ cận và lịch sử đo mắt của từng khách hàng.',
        points: [
            'Sản phẩm theo thuộc tính, biến thể',
            'Lưu thông số, độ cận từng khách',
            'Tồn kho gọng, tròng & mã vạch',
            'Bảo hành, nhắc tái khám định kỳ',
        ],
        img: `${process.env.PUBLIC_URL}/kinhmat.png`,
    },
    {
        id: 'fnb',
        label: 'Quán ăn',
        icon: 'fa-utensils',
        title: 'Quán cà phê, ăn uống',
        desc: 'Order tại bàn, in bill, quản lý ca làm và doanh thu theo từng phiên bán nhanh chóng.',
        points: [
            'Order & in hoá đơn tại quầy',
            'Thực đơn, combo linh hoạt',
            'Quản lý ca làm, đổi ca',
            'Báo cáo doanh thu theo ngày',
        ],
        img: `${process.env.PUBLIC_URL}/food.png`,
    },
    {
        id: 'retail',
        label: 'Bán lẻ / Vật liệu',
        icon: 'fa-warehouse',
        title: 'Cửa hàng bán lẻ, vật liệu',
        desc: 'Quản lý sản phẩm, thuộc tính, biến thể, tồn kho và doanh số bán hàng trong một nơi.',
        points: [
            'Sản phẩm, thuộc tính & biến thể',
            'Theo dõi tồn kho, mã vạch',
            'Bán hàng và lịch sử hoá đơn',
            'Báo cáo lãi lỗ trực quan',
        ],
        img: `${process.env.PUBLIC_URL}/vatlieu.png`,
    },
];

const FEATURES = [
    // Cốt lõi: bán hàng, sản phẩm, kho, khách hàng, báo cáo, thanh toán
    { icon: 'fa-receipt', title: 'Bán hàng & Hoá đơn', desc: 'Tạo order, tính tiền, in bill và xem lịch sử hoá đơn.' },
    {
        icon: 'fa-gauge-high',
        title: 'Tổng quan kinh doanh',
        desc: 'Bức tranh tổng thể tình hình tiệm ngay trên màn hình.',
    },
    { icon: 'fa-box-open', title: 'Sản phẩm & Dịch vụ', desc: 'Tạo dịch vụ, sản phẩm và sắp xếp theo nhóm danh mục.' },
    { icon: 'fa-boxes-stacked', title: 'Quản lý tồn kho', desc: 'Theo dõi số lượng tồn và mã vạch cho từng sản phẩm.' },
    { icon: 'fa-users', title: 'Khách hàng & Lịch sử mua', desc: 'Quản lý hồ sơ khách và toàn bộ lịch sử giao dịch.' },
    { icon: 'fa-chart-line', title: 'Báo cáo đa chiều', desc: 'Báo cáo doanh thu, khách hàng, cuối ngày và bán hàng.' },
    {
        icon: 'fa-qrcode',
        title: 'Thanh toán QR & Ngân hàng',
        desc: 'Cấu hình tài khoản và nhận thanh toán bằng mã QR.',
    },
    { icon: 'fa-book', title: 'Sổ doanh thu & Xuất báo cáo', desc: 'Xem sổ doanh thu và xuất file báo cáo dễ dàng.' },
    // Bán hàng nâng cao
    { icon: 'fa-tags', title: 'Giảm giá trên đơn', desc: 'Áp giảm giá theo % hoặc số tiền cho toàn bộ đơn hàng.' },
    {
        icon: 'fa-sliders',
        title: 'Thuộc tính & Biến thể',
        desc: 'Tạo size, loại và biến thể linh hoạt cho từng sản phẩm.',
    },
    {
        icon: 'fa-calendar-check',
        title: 'Đặt lịch hẹn',
        desc: 'Đặt và xếp lịch theo khung giờ với lưới lịch trực quan.',
    },
    { icon: 'fa-ticket', title: 'Gói dịch vụ', desc: 'Bán gói dịch vụ, trừ buổi tự động và theo dõi sử dụng.' },
    // Khách hàng & chăm sóc
    { icon: 'fa-gift', title: 'Tích điểm', desc: 'Thiết lập quy tắc điểm và theo dõi lịch sử dùng điểm.' },
    { icon: 'fa-star', title: 'Đánh giá & Feedback', desc: 'Nhận và quản lý đánh giá để chăm sóc, giữ chân khách.' },
    { icon: 'fa-user-check', title: 'Check-in khách', desc: 'Khách tự check-in và đăng ký nhanh ngay tại tiệm.' },
    // Nhân sự
    { icon: 'fa-user-tie', title: 'Quản lý nhân viên', desc: 'Quản lý hồ sơ nhân viên và hỗ trợ thao tác tại quầy.' },
    { icon: 'fa-clock', title: 'Ca làm việc', desc: 'Phân ca, sắp xếp và quản lý lịch làm của nhân viên.' },
    { icon: 'fa-coins', title: 'Hoa hồng', desc: 'Thiết lập và tính hoa hồng theo doanh số rõ ràng.' },
    {
        icon: 'fa-money-check-dollar',
        title: 'Bảng lương',
        desc: 'Lập bảng lương và xem chi tiết lương từng nhân viên.',
    },
    // Cài đặt & Hệ thống
    { icon: 'fa-print', title: 'Kết nối máy in', desc: 'Thiết lập và in hoá đơn qua máy in kết nối.' },
];

const STATS = [
    { icon: 'fa-store', value: '3000+', label: 'Khách hàng tin dùng' },
    { icon: 'fa-star', value: '4.9/5', label: 'Đánh giá hài lòng' },
    { icon: 'fa-receipt', value: '1 triệu+', label: 'Hoá đơn xử lý' },
    { icon: 'fa-clock', value: '5 phút', label: 'Làm quen phần mềm' },
    { icon: 'fa-headset', value: '24/7', label: 'Hỗ trợ nhanh chóng' },
    { icon: 'fa-chart-line', value: 'Báo cáo', label: 'Trực quan' },
    { icon: 'fa-boxes-stacked', value: 'Tồn kho', label: 'Chi tiết' },
    { icon: 'fa-wallet', value: 'Thanh toán', label: 'Chính xác' },
    { icon: 'fa-scale-balanced', value: 'Thu chi', label: 'Rõ ràng' },
];

const WHY = [
    {
        icon: 'fa-desktop',
        desc: 'Giao diện tích hợp bán hàng và kế toán — một nơi xử lý đơn, một nơi xem sổ sách, không cần phần mềm thứ hai.',
    },
    {
        icon: 'fa-file-invoice',
        desc: 'Mỗi đơn hàng bán ra tự động ghi vào sổ doanh thu — không cần nhập tay, không lo sai số.',
    },
    {
        icon: 'fa-boxes-stacked',
        desc: 'Tồn kho cập nhật theo thời gian thực, kiểm soát bằng mã vạch — đầu vào, đầu ra minh bạch cho báo cáo thuế.',
    },
    {
        icon: 'fa-right-left',
        desc: 'Sổ thu chi tự động: doanh thu, chi phí, lãi lỗ — đầy đủ dữ liệu để khai thuế đúng hạn, không thiếu chứng từ.',
    },
    {
        icon: 'fa-chart-line',
        desc: 'Báo cáo tài chính theo ngày, tuần, tháng, quý — sẵn sàng cho cuộc gặp kế toán hoặc nộp tờ khai thuế.',
    },
    {
        icon: 'fa-qrcode',
        desc: 'Thanh toán bằng QR, tự động đối soát dòng tiền — không thất thu, không nhầm lẫn khi quyết toán.',
    },
];

// source: 'chplay' | 'appstore' | 'google' — nguồn đánh giá (hiện icon thương hiệu)
// img: đường dẫn ảnh khách (để trống thì hiện avatar chữ cái, thêm ảnh sau)
const TESTIMONIALS = [
    {
        quote: 'Từ ngày dùng Navo Sổ Tiệm, mình quản lý lịch hẹn và hoa hồng thợ rõ ràng hẳn, không còn nhầm lẫn cuối tháng.',
        name: 'Chị Hương',
        role: 'Chủ salon tóc, Hà Nội',
        initial: 'H',
        source: 'chplay',
        img: '',
    },
    {
        quote: 'Gói dịch vụ trừ buổi tự động giúp spa của mình tiết kiệm rất nhiều thời gian đối soát với khách.',
        name: 'Anh Tuấn',
        role: 'Chủ spa, TP.HCM',
        initial: 'T',
        source: 'appstore',
        img: '',
    },
    {
        quote: 'Đặt lịch theo thợ và thư viện mẫu nail giúp tiệm mình chốt khách nhanh, khách quay lại đều hơn hẳn.',
        name: 'Chị Mai',
        role: 'Chủ tiệm nail, Hà Nội',
        initial: 'M',
        source: 'chplay',
        img: '',
    },
    {
        quote: 'Lưu được độ cận và lịch sử đo mắt từng khách, tới ngày là phần mềm tự nhắc tái khám, rất tiện.',
        name: 'Anh Phong',
        role: 'Chủ cửa hàng mắt kính, Hải Phòng',
        initial: 'P',
        source: 'google',
        img: '',
    },
    {
        quote: 'Order tại bàn rồi in bill ngay, cuối ca chốt doanh thu chỉ vài giây, không còn lệch tiền như trước.',
        name: 'Chị Lan',
        role: 'Chủ quán cà phê, Đà Nẵng',
        initial: 'L',
        source: 'chplay',
        img: '',
    },
    {
        quote: 'Quản lý sản phẩm theo thuộc tính, biến thể và tồn kho bằng mã vạch giúp cửa hàng vật liệu của mình gọn gàng hẳn.',
        name: 'Anh Dũng',
        role: 'Chủ cửa hàng vật liệu, Bình Dương',
        initial: 'D',
        source: 'appstore',
        img: '',
    },
    {
        quote: 'Báo cáo lãi lỗ trực quan, mình xem được tình hình cửa hàng mọi lúc ngay trên điện thoại.',
        name: 'Chị Ngọc',
        role: 'Chủ cửa hàng bán lẻ, TP.HCM',
        initial: 'N',
        source: 'google',
        img: '',
    },
    {
        quote: 'Thẻ tích điểm và gói dịch vụ giúp salon giữ chân khách quen, doanh thu tháng nào cũng đều.',
        name: 'Chị Trang',
        role: 'Chủ salon tóc & makeup, Cần Thơ',
        initial: 'T',
        source: 'chplay',
        img: '',
    },
    {
        quote: 'Phân ca và tính lương kỹ thuật viên tự động, mình đỡ hẳn việc tính tay mỗi cuối tháng.',
        name: 'Chị Vy',
        role: 'Chủ spa thẩm mỹ, Nha Trang',
        initial: 'V',
        source: 'appstore',
        img: '',
    },
    {
        quote: 'Thực đơn, combo linh hoạt và đổi ca dễ, nhân viên mới cũng dùng được ngay trong ngày đầu.',
        name: 'Anh Hải',
        role: 'Chủ quán ăn, Hà Nội',
        initial: 'H',
        source: 'google',
        img: '',
    },
];

const REVIEW_SOURCES = {
    chplay: { icon: 'fa-google-play', label: 'Đánh giá trên CH Play' },
    appstore: { icon: 'fa-app-store-ios', label: 'Đánh giá trên App Store' },
    google: { icon: 'fa-google', label: 'Đánh giá trên Google' },
};

const PLANS = [
    {
        name: 'Khởi đầu',
        desc: 'Làm quen miễn phí trước khi quyết định',
        price: 'Miễn phí',
        unit: '15 ngày',
        features: [
            'Trải nghiệm trọn bộ tính năng',
            'Không cần thẻ thanh toán',
            'Bán hàng, hoá đơn & quản lý khách',
            'Báo cáo doanh thu trực quan',
            'Cài đặt nhanh trong vài phút',
        ],
        cta: 'Dùng thử ngay',
        featured: false,
    },
    {
        name: 'Chuyên nghiệp',
        desc: 'Lựa chọn của hầu hết chủ tiệm',
        price: '200K',
        unit: '/tháng',
        features: [
            'Sử dụng không giới hạn mọi tính năng',
            'Lịch hẹn, check-in & quản lý ca làm',
            'Hoa hồng, lương, gói dịch vụ & tích điểm',
            'Báo cáo nâng cao theo thời gian thực',
            'Hỗ trợ 24/7 — đồng hành 100% cùng tiệm',
        ],
        cta: 'Chọn gói này',
        featured: true,
    },
    {
        name: 'Ưu đãi lần đầu',
        desc: 'Dành riêng cho khách đăng ký lần đầu',
        price: '140K',
        oldPrice: '200K',
        unit: '/tháng',
        features: [
            'Đầy đủ tính năng gói Chuyên nghiệp',
            'Tiết kiệm 720K ngay năm đầu sử dụng',
            'Sử dụng không giới hạn, hỗ trợ 24/7',
            'Không ràng buộc, huỷ bất cứ lúc nào',
            'Ưu đãi chỉ áp dụng cho lần đăng ký đầu',
        ],
        cta: 'Nhận ưu đãi',
        sale: true,
        featured: false,
    },
];

const FAQS = [
    {
        q: 'Navo Sổ Tiệm phù hợp với ngành nào?',
        a: [
            'Navo Sổ Tiệm phù hợp với hầu hết mọi ngành nghề kinh doanh, bán hàng và dịch vụ.',
            'Cụ thể như salon tóc, nail, spa, thẩm mỹ, tiệm kính mắt, cửa hàng vật liệu - thiết bị, quán cà phê, ăn uống, các cửa hàng bán lẻ, dịch vụ và nhiều ngành nghề khác. Bạn có thể chọn ngành khi đăng ký để có cấu hình phù hợp.',
        ],
    },
    {
        q: 'Cài đặt phần mềm có khó không?',
        a: 'Bạn có thể dùng phần mềm trực tiếp trên website mà không cần cài đặt, hoặc sử dụng trên điện thoại bằng cách tải app "NAVO Sổ Tiệm" trên CH Play hoặc App Store. Nếu gặp khó khăn, hãy liên hệ nhân viên để được hỗ trợ cài đặt phần mềm trực tiếp 24/7.',
    },
    {
        q: 'Dữ liệu của tôi có an toàn không?',
        a: 'Dữ liệu được lưu trữ an toàn, sao lưu định kỳ và phân quyền rõ ràng giữa chủ tiệm, quản lý và nhân viên.',
    },
    {
        q: 'Tôi có thể dùng thử miễn phí không?',
        a: 'Có. Gói Khởi đầu miễn phí để bạn trải nghiệm toàn bộ tính năng trong vòng 15 ngày. Khi cần tiếp tục sử dụng, bạn có thể nâng cấp bất cứ lúc nào.',
    },
    {
        q: 'Navo Sổ Tiệm có hỗ trợ khi tôi gặp khó khăn không?',
        a: 'Có. Đội ngũ Navo Sổ Tiệm hỗ trợ 24/7 qua Hotline 0886.299.670 và Zalo, đồng hành cùng bạn trong suốt quá trình sử dụng.',
    },
];

export default function HomePage() {
    const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [activeIndustry, setActiveIndustry] = useState('salon');
    const [openFaq, setOpenFaq] = useState(0);
    const [showAllFeatures, setShowAllFeatures] = useState(false);
    const [zoomImg, setZoomImg] = useState(null);
    // Trên mobile chặn đăng ký — mọi CTA "dùng thử/đăng ký" chuyển hướng qua Zalo
    const isMobile = useIsMobile();

    const industry = INDUSTRIES.find((i) => i.id === activeIndustry) || INDUSTRIES[0];

    // Hiệu ứng xuất hiện khi cuộn tới; quét lại khi mở rộng tính năng / đổi ngành
    useScrollReveal([showAllFeatures, activeIndustry]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.email || !formData.phone) {
            setSubmitStatus('error');
            return;
        }
        setIsSubmitting(true);
        setSubmitStatus(null);
        try {
            const result = await EmailService.handleContactForm(formData);
            if (result.success) {
                setSubmitStatus('success');
                setFormData({ name: '', email: '', phone: '', service: '', message: '' });
            } else {
                setSubmitStatus('error');
            }
        } catch (error) {
            console.error('Lỗi gửi form:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <main className={cx('home')}>
            {/* ===== Hero ===== */}
            <section className={cx('home__hero')} aria-label="Giới thiệu NAVO Sổ Tiệm">
                <div className={cx('home__wrap', 'home__hero-grid')}>
                    <div className={cx('home__hero-content', 'home__reveal')}>
                        <span className={cx('home__eyebrow')}>Phần mềm quản lý tiệm thông minh</span>
                        <h1 className={cx('home__hero-title')}>
                            Quản lý tiệm dễ dàng, <span>tăng doanh thu</span> mỗi ngày
                        </h1>
                        <p className={cx('home__hero-desc')}>
                            Đồng hành cùng salon, spa, kính mắt, vật liệu thiết bị, bán lẻ và mọi cửa hàng kinh doanh —
                            đưa toàn bộ nghiệp vụ về một nền tảng duy nhất.
                        </p>
                        <ul className={cx('home__hero-points')}>
                            <li>
                                <i className="fa-solid fa-circle-check"></i> Số hoá sổ sách, cập nhật nhanh trước mọi
                                thay đổi về Thuế
                            </li>
                            <li>
                                <i className="fa-solid fa-circle-check"></i> Minh bạch dòng tiền, tối ưu nghĩa vụ Thuế
                                cho hộ kinh doanh
                            </li>
                            <li>
                                <i className="fa-solid fa-circle-check"></i> Quản lý lịch hẹn, đơn hàng và khách hàng
                                tức thì
                            </li>
                            <li>
                                <i className="fa-solid fa-circle-check"></i> Báo cáo doanh thu, lợi nhuận trực quan theo
                                thời gian thực
                            </li>
                            <li>
                                <i className="fa-solid fa-circle-check"></i> Quản lý nhân viên, kho hàng và đánh giá dễ
                                dàng
                            </li>
                        </ul>
                        <div className={cx('home__hero-cta')}>
                            <a
                                className={cx('btn', 'btn--primary', 'btn--lg')}
                                href={isMobile ? ZALO_LINK : '/admin/register'}
                                {...(isMobile && { target: '_blank', rel: 'noopener noreferrer' })}
                            >
                                Dùng thử miễn phí
                            </a>
                            <a className={cx('btn', 'btn--ghost', 'btn--lg')} href="#features">
                                Khám phá tính năng
                            </a>
                        </div>
                        <div className={cx('home__hero-note')}>
                            <i className="fa-solid fa-circle-check"></i>
                            Miễn phí bắt đầu · Không giới hạn tính năng
                        </div>
                        <div className={cx('home__hero-stores')}>
                            <a className={cx('home__store')} href="/tai-app?os=ios">
                                <i className="fa-brands fa-apple"></i>
                                <span>
                                    <small>Tải trên</small>
                                    <strong>App Store</strong>
                                </span>
                            </a>
                            <a className={cx('home__store')} href="/tai-app?os=android">
                                <i className="fa-brands fa-google-play"></i>
                                <span>
                                    <small>Tải trên</small>
                                    <strong>Google Play</strong>
                                </span>
                            </a>
                        </div>
                    </div>
                    <div className={cx('home__hero-visual')}>
                        <div className={cx('home__hero-panel')}>
                            <div className={cx('home__phone', 'home__phone--left')}>
                                <img src={orderapp} alt="Màn hình order Navo Sổ Tiệm" />
                            </div>
                            <div className={cx('home__phone', 'home__phone--center')}>
                                <img src={homeapp} alt="Màn hình trang chủ Navo Sổ Tiệm" />
                            </div>
                            <div className={cx('home__phone', 'home__phone--right')}>
                                <img src={billapp} alt="Màn hình hoá đơn Navo Sổ Tiệm" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== Trust / stats (marquee) ===== */}
            <section className={cx('home__trust')} aria-label="Số liệu nổi bật">
                <div className={cx('home__marquee')}>
                    <div className={cx('home__marquee-track')}>
                        {[...STATS, ...STATS, ...STATS].map((s, idx) => (
                            <div className={cx('home__stat')} key={idx} aria-hidden={idx >= STATS.length}>
                                <div className={cx('home__stat-icon')}>
                                    <i className={`fa-solid ${s.icon}`}></i>
                                </div>
                                <div className={cx('home__stat-body')}>
                                    <strong>{s.value}</strong>
                                    <span>{s.label}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== Why choose ===== */}
            <section className={cx('home__section')}>
                <div className={cx('home__wrap')}>
                    <div className={cx('home__section-head')} data-reveal>
                        <span className={cx('home__eyebrow')}>Vì sao chọn Navo Sổ Tiệm</span>
                        <h2 className={cx('home__title')}>
                            Đồng hành cùng Hộ kinh doanh thích ứng linh hoạt trước mọi thay đổi về Thuế và pháp lý
                        </h2>
                        <p className={cx('home__subtitle')}>
                            Bán hàng, kế toán, khai thuế — tất cả trong một ứng dụng.
                        </p>
                    </div>
                    <div className={cx('home__why-visual')} data-reveal="zoom">
                        <img src={`${process.env.PUBLIC_URL}/sohoa.png`} alt="Số hoá tuân thủ Thuế cùng Navo Sổ Tiệm" />
                    </div>
                    <ul className={cx('home__why-list')}>
                        {WHY.map((w, idx) => (
                            <li key={w.desc} data-reveal style={{ '--reveal-delay': `${idx * 80}ms` }}>
                                <i className={`fa-solid ${w.icon}`}></i>
                                <span>{w.desc}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* ===== Features ===== */}
            <section id="features" className={cx('home__section', 'home__section--soft')} aria-label="Tính năng chính">
                <div className={cx('home__wrap')}>
                    <div className={cx('home__section-head')} data-reveal>
                        <span className={cx('home__eyebrow')}>Tính năng</span>
                        <h2 className={cx('home__title')}>Tính năng nổi bật</h2>
                        <p className={cx('home__subtitle')}>Tất cả nghiệp vụ của tiệm gói gọn trong một nền tảng.</p>
                    </div>
                    <div className={cx('home__features-grid', !showAllFeatures && 'home__features-grid--collapsed')}>
                        {FEATURES.map((f, idx) => (
                            <div
                                className={cx('home__feature')}
                                key={f.title}
                                data-reveal
                                style={{ '--reveal-delay': `${(idx % 5) * 60}ms` }}
                            >
                                <div className={cx('home__feature-head')}>
                                    <div className={cx('home__feature-icon')}>
                                        <i className={`fa-solid ${f.icon}`}></i>
                                    </div>
                                    <h3>{f.title}</h3>
                                </div>
                                <p>{f.desc}</p>
                            </div>
                        ))}
                    </div>
                    <div className={cx('home__features-more')}>
                        <button
                            type="button"
                            className={cx('btn', 'btn--ghost')}
                            onClick={() => setShowAllFeatures((v) => !v)}
                            aria-expanded={showAllFeatures}
                        >
                            {showAllFeatures ? 'Ẩn bớt' : 'Xem thêm tính năng'}
                            <i className={`fa-solid ${showAllFeatures ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== Industry solutions ===== */}
            <section className={cx('home__section')} aria-label="Giải pháp theo ngành">
                <div className={cx('home__wrap')}>
                    <div className={cx('home__section-head')} data-reveal>
                        <span className={cx('home__eyebrow')}>Giải pháp theo ngành</span>
                        <h2 className={cx('home__title')}>Phù hợp với mọi mô hình kinh doanh</h2>
                        <p className={cx('home__subtitle')}>Chọn ngành của bạn để xem Navo Sổ Tiệm hỗ trợ những gì.</p>
                    </div>
                    <div className={cx('home__tabs')} role="tablist">
                        {INDUSTRIES.map((i) => (
                            <button
                                key={i.id}
                                className={cx('home__tab', activeIndustry === i.id && 'is-active')}
                                onClick={() => setActiveIndustry(i.id)}
                                role="tab"
                                aria-selected={activeIndustry === i.id}
                            >
                                <i className={`fa-solid ${i.icon}`}></i>
                                {i.label}
                            </button>
                        ))}
                    </div>
                    <div className={cx('home__industry')}>
                        <div className={cx('home__industry-content')} data-reveal="left">
                            <h3>{industry.title}</h3>
                            <p>{industry.desc}</p>
                            <ul className={cx('home__industry-list')}>
                                {industry.points.map((p) => (
                                    <li key={p}>
                                        <i className="fa-solid fa-circle-check"></i>
                                        {p}
                                    </li>
                                ))}
                            </ul>
                            <a className={cx('btn', 'btn--primary', 'home__industry-cta')} href="#contact">
                                Nhận tư vấn cho ngành này
                            </a>
                        </div>
                        <button
                            type="button"
                            className={cx('home__industry-visual')}
                            onClick={() => setZoomImg({ src: industry.img, alt: industry.title })}
                            aria-label={`Phóng to ảnh ${industry.title}`}
                            data-reveal="right"
                        >
                            <img src={industry.img} alt={industry.title} />
                            <span className={cx('home__zoom-tap')} aria-hidden="true">
                                <span className={cx('home__zoom-ripple')}></span>
                                <i className="fa-regular fa-hand-pointer" style={{ color: '#0098f6' }}></i>
                            </span>
                            <span className={cx('home__zoom-label')}>Nhấn để xem ảnh</span>
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== Showcase ===== */}
            <section className={cx('home__section', 'home__section--showcase')} aria-label="Giao diện ứng dụng">
                <div className={cx('home__wrap', 'home__showcase')}>
                    <div className={cx('home__showcase-content')} data-reveal="left">
                        <span className={cx('home__eyebrow')}>Trực quan</span>
                        <h2 className={cx('home__title')} style={{ textAlign: 'left' }}>
                            Giao diện dễ dùng, thao tác nhanh
                        </h2>
                        <p className={cx('home__subtitle')} style={{ textAlign: 'left' }}>
                            Thiết kế hiện đại, tối ưu cho thao tác tại quầy và quản lý từ xa trên điện thoại, máy tính
                            bảng.
                        </p>
                        <ul className={cx('home__showcase-list')}>
                            <li>
                                <i className="fa-solid fa-sliders"></i>
                                <div>
                                    <strong>Thiết lập linh hoạt</strong>
                                    <span>Dịch vụ, combo, bảng giá tuỳ chỉnh theo tiệm.</span>
                                </div>
                            </li>
                            <li>
                                <i className="fa-solid fa-user-clock"></i>
                                <div>
                                    <strong>Quản lý ca & hoa hồng</strong>
                                    <span>Phân ca, tính hoa hồng nhân viên rõ ràng.</span>
                                </div>
                            </li>
                            <li>
                                <i className="fa-solid fa-bell"></i>
                                <div>
                                    <strong>Thông báo tự động</strong>
                                    <span>Tích hợp Zalo OA, nhắc lịch và gửi thông báo.</span>
                                </div>
                            </li>
                            <li>
                                <i className="fa-solid fa-file-invoice-dollar"></i>
                                <div>
                                    <strong>Hoá đơn & thanh toán</strong>
                                    <span>Quản lý lịch sử hoá đơn, thanh toán dễ dàng.</span>
                                </div>
                            </li>
                        </ul>
                        <a className={cx('btn', 'btn--primary')} href="#contact">
                            Nhận tư vấn Navo Sổ Tiệm
                        </a>
                    </div>
                    <div className={cx('home__showcase-visual')} data-reveal="right">
                        <img src={`${process.env.PUBLIC_URL}/thiet-bi.png`} alt="Thiết bị dùng Navo Sổ Tiệm" />
                    </div>
                </div>
            </section>

            {/* ===== Testimonials ===== */}
            <section className={cx('home__section', 'home__section--soft')} aria-label="Cảm nhận khách hàng">
                <div className={cx('home__wrap')}>
                    <div className={cx('home__section-head')} data-reveal>
                        <span className={cx('home__eyebrow')}>Khách hàng nói gì</span>
                        <h2 className={cx('home__title')}>Được tin dùng bởi hàng nghìn chủ tiệm</h2>
                        <p className={cx('home__subtitle')}>Những chia sẻ thật từ người đang dùng Navo Sổ Tiệm mỗi ngày.</p>
                    </div>
                    <div className={cx('home__testimonials')}>
                        <div className={cx('home__testimonials-track')}>
                            {[...TESTIMONIALS, ...TESTIMONIALS].map((t, idx) => {
                                const source = REVIEW_SOURCES[t.source];
                                return (
                                    <figure
                                        className={cx('home__testimonial')}
                                        key={`${t.name}-${idx}`}
                                        aria-hidden={idx >= TESTIMONIALS.length}
                                    >
                                        <div className={cx('home__testimonial-top')}>
                                            <div className={cx('home__testimonial-stars')}>{'★★★★★'}</div>
                                            {source && (
                                                <span
                                                    className={cx('home__testimonial-source', `is-${t.source}`)}
                                                    title={source.label}
                                                >
                                                    <i className={`fa-brands ${source.icon}`}></i>
                                                </span>
                                            )}
                                        </div>
                                        <blockquote>“{t.quote}”</blockquote>
                                        <figcaption>
                                            <div className={cx('home__testimonial-avatar')}>
                                                {t.img ? <img src={t.img} alt={t.name} /> : t.initial}
                                            </div>
                                            <div>
                                                <div className={cx('home__testimonial-name')}>{t.name}</div>
                                                <div className={cx('home__testimonial-role')}>{t.role}</div>
                                            </div>
                                        </figcaption>
                                    </figure>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== Pricing ===== */}
            <section id="pricing" className={cx('home__section')} aria-label="Bảng giá">
                <div className={cx('home__wrap')}>
                    <div className={cx('home__section-head')} data-reveal>
                        <span className={cx('home__eyebrow')}>Bảng giá</span>
                        <h2 className={cx('home__title')}>Chọn gói phù hợp với tiệm của bạn</h2>
                        <p className={cx('home__subtitle')}>Bắt đầu miễn phí, nâng cấp khi tiệm phát triển.</p>
                    </div>
                    <div className={cx('home__pricing')}>
                        {PLANS.map((p, idx) => (
                            <div
                                className={cx(
                                    'home__plan',
                                    p.featured && 'home__plan--featured',
                                    p.sale && 'home__plan--sale',
                                )}
                                key={p.name}
                                data-reveal
                                style={{ '--reveal-delay': `${idx * 100}ms` }}
                            >
                                {p.featured && <span className={cx('home__plan-tag')}>Phổ biến nhất</span>}
                                {p.sale && (
                                    <span className={cx('home__plan-tag', 'home__plan-tag--sale')}>Đang giảm giá</span>
                                )}
                                <div className={cx('home__plan-name')}>{p.name}</div>
                                <div className={cx('home__plan-desc')}>{p.desc}</div>
                                <div className={cx('home__plan-price')}>
                                    {p.oldPrice && <del className={cx('home__plan-old-price')}>{p.oldPrice}</del>}
                                    {p.price}
                                    <span>{p.unit}</span>
                                </div>
                                <ul className={cx('home__plan-features')}>
                                    {p.features.map((f) => (
                                        <li key={f}>
                                            <i className="fa-solid fa-check"></i>
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <a
                                    className={cx('btn', p.featured ? 'btn--primary' : 'btn--ghost')}
                                    href={!isMobile && p.cta === 'Dùng thử ngay' ? '/admin/register' : ZALO_LINK}
                                    {...((isMobile || p.cta !== 'Dùng thử ngay') && {
                                        target: '_blank',
                                        rel: 'noopener noreferrer',
                                    })}
                                >
                                    {p.cta}
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== FAQ ===== */}
            <section id="faq" className={cx('home__section', 'home__section--soft')} aria-label="Câu hỏi thường gặp">
                <div className={cx('home__wrap')}>
                    <div className={cx('home__section-head')} data-reveal>
                        <span className={cx('home__eyebrow')}>Hỏi đáp</span>
                        <h2 className={cx('home__title')}>Câu hỏi thường gặp</h2>
                        <p className={cx('home__subtitle')}>Những thắc mắc phổ biến trước khi bắt đầu với Navo Sổ Tiệm.</p>
                    </div>
                    <div className={cx('home__faq')}>
                        {FAQS.map((item, idx) => (
                            <div
                                className={cx('home__faq-item')}
                                key={item.q}
                                data-reveal
                                data-open={openFaq === idx || undefined}
                                style={{ '--reveal-delay': `${idx * 70}ms` }}
                            >
                                <button
                                    className={cx('home__faq-q')}
                                    onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                                    aria-expanded={openFaq === idx}
                                >
                                    {item.q}
                                    <i className="fa-solid fa-plus"></i>
                                </button>
                                <div className={cx('home__faq-a')}>
                                    {Array.isArray(item.a)
                                        ? item.a.map((para, i) => <p key={i}>{para}</p>)
                                        : item.a}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== Contact ===== */}
            <section id="contact" className={cx('home__section')} aria-label="Liên hệ với chúng tôi">
                <div className={cx('home__wrap')}>
                    <div className={cx('home__section-head')} data-reveal>
                        <span className={cx('home__eyebrow')}>Liên hệ</span>
                        <h2 className={cx('home__title')}>Liên hệ với chúng tôi</h2>
                        <p className={cx('home__subtitle')}>
                            Để lại thông tin, đội ngũ Navo Sổ Tiệm sẽ liên hệ tư vấn miễn phí.
                        </p>
                    </div>
                    <div className={cx('home__contact-grid')}>
                        <form className={cx('home__contact-form')} onSubmit={handleSubmit} data-reveal="left">
                            <input
                                className={cx('home__input')}
                                name="name"
                                placeholder="Họ và tên *"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                            />
                            <input
                                className={cx('home__input')}
                                type="email"
                                name="email"
                                placeholder="Email *"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                            />
                            <input
                                className={cx('home__input')}
                                type="tel"
                                name="phone"
                                placeholder="Số điện thoại *"
                                value={formData.phone}
                                onChange={handleInputChange}
                                required
                            />
                            <textarea
                                className={cx('home__textarea')}
                                rows={5}
                                name="message"
                                placeholder="Nội dung"
                                value={formData.message}
                                onChange={handleInputChange}
                                required
                            />
                            {submitStatus === 'success' && (
                                <div className={cx('home__status', 'home__status--success')}>
                                    Cảm ơn bạn đã liên hệ! Chúng tôi sẽ phản hồi trong thời gian sớm nhất.
                                </div>
                            )}
                            {submitStatus === 'error' && (
                                <div className={cx('home__status', 'home__status--error')}>
                                    Có lỗi xảy ra khi gửi tin nhắn. Liên hệ trực tiếp qua Hotline hoặc vui lòng thử lại
                                    sau.
                                </div>
                            )}
                            <button
                                className={cx('btn', 'btn--primary', 'home__submit')}
                                type="submit"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Đang gửi...' : 'Gửi tin nhắn'}
                            </button>
                        </form>
                        <div className={cx('home__contact-info')} data-reveal="right">
                            <div className={cx('home__info-row')}>
                                <i className="fa-solid fa-phone"></i>
                                <div>
                                    <strong>Hotline</strong>
                                    <span>0886.299.670</span>
                                </div>
                            </div>
                            <div className={cx('home__info-row')}>
                                <i className="fa-solid fa-envelope"></i>
                                <div>
                                    <strong>Email</strong>
                                    <span>navosotiem@gmail.com</span>
                                </div>
                            </div>
                            <div className={cx('home__info-row')}>
                                <i className="fa-solid fa-location-dot"></i>
                                <div>
                                    <strong>Địa chỉ</strong>
                                    <span>HD Mon City, Nguyễn Cơ Thạch, Mỹ Đình 2, Hà Nội</span>
                                </div>
                            </div>
                            <div className={cx('home__contact-actions')}>
                                <a
                                    className={cx('home__social')}
                                    href="https://www.facebook.com/NAVOSotiemkinhdoanh"
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label="Facebook"
                                >
                                    <i className="fa-brands fa-facebook-f"></i>
                                </a>
                                <a
                                    className={cx('home__zalo-btn')}
                                    href="https://zalo.me/0387556219"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Liên hệ Zalo 24/7
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {zoomImg && (
                <div
                    className={cx('home__lightbox')}
                    onClick={() => setZoomImg(null)}
                    role="dialog"
                    aria-modal="true"
                    aria-label={zoomImg.alt}
                >
                    <button
                        type="button"
                        className={cx('home__lightbox-close')}
                        onClick={() => setZoomImg(null)}
                        aria-label="Đóng"
                    >
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                    <img
                        className={cx('home__lightbox-img')}
                        src={zoomImg.src}
                        alt={zoomImg.alt}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}

            <FloatingContact />
        </main>
    );
}
